'use client';

import { useState } from 'react';
import { X } from 'lucide-react';
import ModalWrapper from './ModalWrapper';
import { useActionKey } from '@/hooks/useActionKey';
import { useHyperliquidMain } from '@/hooks/useHyperliquidMain';
import { computeAddress } from 'ethers';
import { useAccount } from 'wagmi';

interface EstablishConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EstablishConnectionModal({ isOpen, onClose }: EstablishConnectionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { generateActionKey, setActionKey } = useActionKey();
  const { address: walletAddress } = useAccount();

  const { approveAgent } = useHyperliquidMain();

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!walletAddress) {
        setError('Please connect your wallet first');
        return;
      }

      // Action key 생성
      const { actionPublicKey, actionPrivateKey, expiredAt } = generateActionKey(Date.now() + 3600 * 1000);

      const agentAddress = computeAddress(actionPublicKey);

      // 에이전트 승인 (사용자 지갑으로 서명)
      const result = await approveAgent(agentAddress, 'k-perps-temp-key');
      if (result.status === 'ok') {
        setActionKey(expiredAt, actionPublicKey, actionPrivateKey);
        onClose();
      }
    } catch (err: any) {
      console.error('❌ Connection failed:', err);
      setError(err.message || 'Failed to establish connection');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
    >
      <div
        className='glass-neon glow-effect animate-slideUp w-full max-w-lg rounded-2xl p-8'
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-gray-400 transition-colors hover:text-white'
        >
          <X className='h-6 w-6' />
        </button>

        {/* Title */}
        <h2 className='mb-6 text-center text-3xl font-bold'>Establish Connection</h2>

        {/* Description */}
        <p className='mb-8 text-center leading-relaxed text-purple-300'>
          This signature is <span className='font-bold'>gas-free</span> to send. It opens a{' '}
          <span className='font-bold'>decentralized channel</span> for
          <span className='font-bold'> gas-free and instantaneous trading</span>.
        </p>

        {/* Error Message */}
        {error && (
          <div className='mb-4 rounded-lg border border-red-500/50 bg-red-500/20 p-3 text-sm text-red-400'>{error}</div>
        )}

        {/* Info Message */}
        {!walletAddress && (
          <div className='mb-4 rounded-lg border border-yellow-500/50 bg-yellow-500/20 p-3 text-sm text-yellow-400'>
            Please connect your wallet first using the Connect button in the header.
          </div>
        )}

        {/* Connect Button */}
        <button
          onClick={handleConnect}
          disabled={isLoading || !walletAddress}
          className={`flex w-full items-center justify-center gap-2 rounded-xl border-2 py-4 text-lg font-bold transition-all ${
            isLoading || !walletAddress
              ? 'cursor-not-allowed border-gray-600 bg-gray-800/50 text-gray-400'
              : 'neon-button-cyan border-purple-400 bg-purple-500/10'
          }`}
        >
          <span>{isLoading ? 'Connecting...' : 'Establish Connection'}</span>
        </button>

        {/* Additional Info */}
        <div className='mt-6 text-center'>
          <p className='text-xs text-gray-500'>By connecting, you join the HPPC Perp Dex</p>
        </div>
      </div>
    </ModalWrapper>
  );
}
